import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '../common/model/dto/order/order';
import { RecordColumn } from '../common/model/ui/record-list.type';
import {
  getFulfillmentStatusTone,
  getOrderStatusTone,
  getPaymentStatusTone,
} from '../common/utils/services/order-status-utils';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvStatusChip from '../components/UI/atoms/OvStatusChip';
import OvOrderCustomerSummary from '../components/UI/molecules/OvOrderCustomerSummary';
import OvOrderDeliveryStatus from '../components/UI/molecules/OvOrderDeliveryStatus';
import OvOrderIdentity from '../components/UI/molecules/OvOrderIdentity';
import OvOrderTotalSummary from '../components/UI/molecules/OvOrderTotalSummary';

export function useOrderListColumns(): RecordColumn<Order>[] {
  const { t } = useTranslation();

  return [
    {
      id: 'order',
      header: t('orders.tableHeaders.order'),
      sortKey: 'internal_id',
      renderCell: (order, detailsPath = '') => (
        <OvOrderIdentity order={order} detailsPath={detailsPath} />
      ),
    },
    {
      id: 'customer',
      header: t('orders.tableHeaders.customerAndOrderedDate'),
      sortKey: 'ordered_at',
      renderCell: (order) => <OvOrderCustomerSummary order={order} />,
    },
    {
      id: 'total',
      header: t('orders.tableHeaders.totalAndDiscount'),
      align: 'right',
      renderCell: (order) => <OvOrderTotalSummary order={order} />,
    },
    {
      id: 'status',
      header: t('orders.tableHeaders.orderStatus'),
      renderCell: (order) => (
        <OvStatusChip
          label={order.status}
          tone={getOrderStatusTone(order.status)}
        />
      ),
    },
    {
      id: 'payment',
      header: t('orders.tableHeaders.paymentStatus'),
      renderCell: (order) => (
        <OvStatusChip
          label={order.payment_status}
          tone={getPaymentStatusTone(order.payment_status)}
        />
      ),
    },
    {
      id: 'fulfillment',
      header: t('orders.tableHeaders.fulfillmentStatus'),
      renderCell: (order) => (
        <OvStatusChip
          label={order.fulfillment_status}
          tone={getFulfillmentStatusTone(order.fulfillment_status)}
        />
      ),
    },
    {
      id: 'delivery',
      header: t('orders.tableHeaders.delivery'),
      renderCell: (order) => <OvOrderDeliveryStatus order={order} showDetail />,
    },
    {
      id: 'action',
      header: t('orders.tableHeaders.action'),
      isHeaderHidden: true,
      renderCell: (order, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          aria-label={t('orders.detailsFor', { id: order.internal_id })}
        >
          {t('orders.orderDetailsCta')}
        </OvPillLink>
      ),
    },
  ];
}
